import React, { useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { useContent } from "../context/ContentContext";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";

const Gallery = () => {
  const { projects, projectCategories } = useContent();
  const [active, setActive] = useState("All");
  const [album, setAlbum] = useState(null); // selected project
  const [current, setCurrent] = useState(0); // index within album photos

  const filtered = active === "All" ? projects : projects.filter((p) => p.category === active);

  const openAlbum = (p) => {
    setAlbum(p);
    setCurrent(0);
  };
  const closeAlbum = () => setAlbum(null);

  const photos = album ? (album.photos && album.photos.length ? album.photos : [album.image]) : [];

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % photos.length);
  }, [photos.length]);
  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + photos.length) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    if (!album) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeAlbum();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [album, next, prev]);

  return (
    <main className="pt-[76px]">
      <Seo
        title="Project Gallery"
        description="Browse completed projects by Fletcher & Stone — new builds, extensions, loft conversions, refurbishments, basement construction and heritage restorations across London, The Home Counties and The Cotswolds."
        path="/gallery"
        image="/hero-structural-works.jpg"
      />
      <PageHero
        eyebrow="Gallery"
        title="A portfolio of fine building work"
        subtitle="A selection of new builds, extensions, refurbishments and heritage restorations delivered across London, The Home Counties and The Cotswolds."
        image="/hero-structural-works.jpg"
      />

      <section className="max-w-[1240px] mx-auto px-5 md:px-8 py-20 md:py-28">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 md:gap-4 justify-center mb-14">
          {projectCategories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`font-body uppercase tracking-[0.14em] text-[11.5px] px-5 py-2.5 border transition-all duration-300 ${
                active === c
                  ? "bg-[#23211e] text-white border-[#23211e]"
                  : "bg-transparent text-[#6b665d] border-[#e2ddd4] hover:border-[#94664f] hover:text-[#94664f]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Album covers */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => {
            const count = p.photos && p.photos.length ? p.photos.length : 1;
            return (
              <button key={p.id} onClick={() => openAlbum(p)} className="group text-left relative overflow-hidden">
                <div className="h-[300px] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                </div>
                {/* photo-count badge */}
                <span className="absolute top-4 right-4 flex items-center gap-1.5 bg-black/55 backdrop-blur text-white font-body text-[11px] tracking-[0.08em] px-2.5 py-1.5">
                  <Images size={13} strokeWidth={1.6} /> {count}
                </span>
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 p-6">
                  <span className="overline !text-[#e6d3c6]">{p.category} &middot; {p.location}</span>
                  <h3 className="font-display text-white text-[26px] mt-1">{p.title}</h3>
                  <span className="inline-flex items-center gap-1.5 mt-2 text-white/80 font-body uppercase tracking-[0.14em] text-[10.5px] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    View album
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Album lightbox */}
      {album && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col p-4 md:p-8" onClick={closeAlbum}>
          <button className="absolute top-5 right-5 z-10 text-white/80 hover:text-white" onClick={closeAlbum} aria-label="Close">
            <X size={30} strokeWidth={1.2} />
          </button>

          <div className="flex-1 flex flex-col min-h-0" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="text-center mb-4 shrink-0">
              <span className="overline !text-[#e6d3c6]">{album.category} &middot; {album.location}</span>
              <h3 className="font-display text-white text-[26px] md:text-[32px] mt-1">{album.title}</h3>
            </div>

            {/* Main image with arrows */}
            <div className="relative flex-1 flex items-center justify-center min-h-0">
              {photos.length > 1 && (
                <button onClick={prev} aria-label="Previous" className="absolute left-0 md:left-2 z-10 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white transition-colors">
                  <ChevronLeft size={26} strokeWidth={1.4} />
                </button>
              )}
              <img src={photos[current]} alt={`${album.title} ${current + 1}`} className="max-w-full max-h-full object-contain" />
              {photos.length > 1 && (
                <button onClick={next} aria-label="Next" className="absolute right-0 md:right-2 z-10 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/25 text-white transition-colors">
                  <ChevronRight size={26} strokeWidth={1.4} />
                </button>
              )}
            </div>

            {/* Thumbnails */}
            <div className="shrink-0 mt-5 flex items-center justify-center gap-3 flex-wrap">
              {photos.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-16 h-16 md:w-20 md:h-20 overflow-hidden border-2 transition-all duration-300 ${
                    i === current ? "border-[#94664f] opacity-100" : "border-transparent opacity-55 hover:opacity-90"
                  }`}
                >
                  <img src={src} alt={`thumbnail ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <p className="text-center text-white/50 font-body text-[12px] tracking-[0.12em] mt-3 shrink-0">
              {current + 1} / {photos.length}
            </p>
          </div>
        </div>
      )}
    </main>
  );
};

export default Gallery;
